/**
 * @file ChatBox.tsx
 * @description
 * Component for displaying and sending chat messages within a game room.
 * Displays a list of messages and an input field for sending new messages.
 *
 * @dependencies
 * - React: Standard React library.
 * - react: Used for hooks like useState, useEffect, useRef.
 *
 * @notes
 * - Basic styling applied directly; will integrate theme/CSS later.
 * - Automatically scrolls to the latest message.
 * - Handles rate limiting warnings (although actual rate limiting logic is server-side).
 */

import React, { useState, useEffect, useRef, FormEvent, ChangeEvent } from 'react';

// Define the structure of a chat message as received from the backend
interface ChatMessage {
  senderId: string; // ID of the sender (e.g., socket ID)
  senderName: string; // Display name of the sender
  text: string; // The message content
  timestamp: number; // Unix timestamp (milliseconds) when the message was sent
}

interface ChatBoxProps {
  /** Array of chat messages to display. */
  messages: ChatMessage[];
  /** Function to call when a new message is sent. */
  onSendMessage: (text: string) => void;
  /** Optional error message to display (e.g., rate limit warnings). */
  errorMessage?: string | null;
  /** Boolean indicating if the current user is a player (can send messages). Spectators cannot chat. */
  canSendMessages: boolean;
}

const ChatBox: React.FC<ChatBoxProps> = ({ messages, onSendMessage, errorMessage, canSendMessages }) => {
  const [messageInput, setMessageInput] = useState('');
  // Ref to the chat message container for auto-scrolling
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Effect to scroll to the bottom whenever new messages are added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]); // Re-run effect when messages array changes

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMessageInput(event.target.value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedMessage = messageInput.trim();
    if (trimmedMessage && canSendMessages) { // Ensure message is not empty and user can send
      onSendMessage(trimmedMessage);
      setMessageInput(''); // Clear input after sending
    }
  };

  // Basic styling
  const chatContainerStyle: React.CSSProperties = {
    width: '300px', // Fixed width for now
    height: '200px', // Fixed height for now
    border: '1px solid #ccc',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    overflow: 'hidden',
    fontFamily: 'sans-serif',
    fontSize: '0.9rem',
  };

  const messagesListStyle: React.CSSProperties = {
    flexGrow: 1, // Take up available space
    overflowY: 'auto', // Enable scrolling
    padding: '10px',
    listStyle: 'none',
    margin: 0,
  };

  const messageStyle: React.CSSProperties = {
    marginBottom: '8px',
    wordBreak: 'break-word', // Prevent long words from overflowing
  };

  const senderNameStyle: React.CSSProperties = {
    fontWeight: 'bold',
    marginRight: '5px',
    color: '#333', // Darker color for name
  };

   const errorStyle: React.CSSProperties = {
       color: 'red',
       fontSize: '0.8em',
       marginBottom: '8px',
       padding: '0 10px',
   };


  const inputAreaStyle: React.CSSProperties = {
    padding: '10px',
    borderTop: '1px solid #ccc',
    display: 'flex',
    backgroundColor: '#f0f0f0',
  };

  const inputStyle: React.CSSProperties = {
    flexGrow: 1,
    padding: '8px',
    marginRight: '8px',
    border: '1px solid #bbb',
    borderRadius: '4px',
    outline: 'none',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 15px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: canSendMessages ? 'pointer' : 'not-allowed',
    opacity: canSendMessages ? 1 : 0.6,
  };

  return (
    <div style={chatContainerStyle} aria-label="Chat box">
      <ul style={messagesListStyle} aria-live="polite"> {/* aria-live for screen readers */}
        {messages.map((msg, index) => (
          <li key={index} style={messageStyle}>
            <span style={senderNameStyle}>{msg.senderName}:</span>
            <span>{msg.text}</span>
          </li>
        ))}
        {/* Invisible element to scroll to */}
        <div ref={messagesEndRef} />
      </ul>
      {errorMessage && <div style={errorStyle} aria-atomic="true">{errorMessage}</div>} {/* aria-atomic indicates the entire region should be read */}
      <form onSubmit={handleSubmit} style={inputAreaStyle}>
        <input
          type="text"
          value={messageInput}
          onChange={handleInputChange}
          placeholder={canSendMessages ? "Type a message..." : "Spectators cannot chat."}
          style={inputStyle}
          disabled={!canSendMessages} // Disable input if cannot send messages
          aria-label="Chat input"
        />
        <button type="submit" style={buttonStyle} disabled={!canSendMessages}>
          Send
        </button>
      </form>
    </div>
  );
};

export default ChatBox;